import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Stack,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { useConversations } from '@/hooks/useConversations';
import { formatOrderDate } from '@liminal/utils/date';
import { UserIdLink } from '@/components/ui/UserIdLink';
import type { AdminConversationSummary } from '@/api/types';

const PAGE_SIZE = 25;
const COLUMN_COUNT = 7;

function fullName(c: AdminConversationSummary): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ');
}

/**
 * Sorts the page on screen — nim returns conversations most-recently-updated
 * first and takes no sort parameter, so this reorders the 25 rows fetched.
 */
const SORT_ACCESSORS = {
  title: (c: AdminConversationSummary) => c.title,
  email: (c: AdminConversationSummary) => c.email,
  name: (c: AdminConversationSummary) => fullName(c),
  userId: (c: AdminConversationSummary) => c.userId,
  messageCount: (c: AdminConversationSummary) => c.messageCount ?? 0,
  flagCount: (c: AdminConversationSummary) => c.flagCount ?? 0,
  updatedAt: (c: AdminConversationSummary) => c.updatedAt,
};

const SEARCH_DEFAULTS = { q: '' };

export function ConversationsPage() {
  const navigate = useNavigate();
  const [{ q: search }, setParams] = useUrlState(SEARCH_DEFAULTS);
  const pagination = useCursorPagination(PAGE_SIZE);

  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isError } = useConversations(
    debouncedSearch,
    PAGE_SIZE,
    pagination.pageToken ?? '',
  );
  const totalCount = data?.totalCount ?? 0;
  const nextPageToken = data?.nextPageToken ?? '';

  const resetPaging = pagination.reset;

  // Most recently updated first, matching the order nim already returns.
  const { rows: conversations, sort } = useTableSort(
    data?.conversations ?? [],
    SORT_ACCESSORS,
    'updatedAt',
    'desc',
  );

  return (
    <Stack spacing={3}>
      {/* Filter */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={search}
          onChange={(value) => {
            setParams({ q: value });
            resetPaging();
          }}
          placeholder="Search by email, name, or user ID"
          width={340}
        />
        {totalCount > 0 && (
          <Typography variant="body2" color="text.disabled">
            {totalCount === 1 ? '1 conversation' : `${totalCount} conversations`}
          </Typography>
        )}
      </Stack>

      {/* Data Table */}
      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell column="title" sort={sort}>
                Conversation
              </SortableTableCell>
              <SortableTableCell column="email" sort={sort}>
                Email
              </SortableTableCell>
              <SortableTableCell column="name" sort={sort}>
                Name
              </SortableTableCell>
              <SortableTableCell column="userId" sort={sort}>
                User
              </SortableTableCell>
              <SortableTableCell column="messageCount" sort={sort} align="right">
                Messages
              </SortableTableCell>
              <SortableTableCell column="flagCount" sort={sort} align="right">
                Open flags
              </SortableTableCell>
              <SortableTableCell column="updatedAt" sort={sort}>
                Updated
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMN_COUNT}
            isLoading={isLoading}
            isError={isError}
            isEmpty={conversations.length === 0}
            loadingRows={8}
            emptyMessage="No conversations found. Try adjusting your search."
            errorMessage="Could not load conversations."
          >
            {conversations.map((c) => (
              <TableRow
                key={c.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => c.id && navigate(`/conversations/${c.id}`)}
              >
                <TableCell>{c.title || 'Untitled'}</TableCell>
                <TableCell>{c.email || '—'}</TableCell>
                <TableCell>{fullName(c) || '—'}</TableCell>
                <TableCell>
                  <UserIdLink userId={c.userId} />
                </TableCell>
                <TableCell align="right">{c.messageCount ?? 0}</TableCell>
                <TableCell align="right">
                  {c.flagCount ? (
                    <StatusPill label={String(c.flagCount)} tone="warning" icon="flag" size="sm" />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{c.updatedAt ? formatOrderDate(c.updatedAt) : '—'}</TableCell>
              </TableRow>
            ))}
          </DataTableBody>
        </Table>
      </Card>

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={conversations.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />
    </Stack>
  );
}
